/**
 * @fileoverview Test Calendar Page
 * @description Main page for the Test Calendar feature
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { CalendarGrid, CalendarToolbar, EventFormDialog, EventDetailDialog } from '../components';
import { useCalendar } from '../hooks/useCalendar';
import { CALENDAR_VIEWS } from '../constants';
import { navigateDate, formatForApi, startOfMonth, endOfMonth, startOfWeek, addDays, startOfDay, endOfDay } from '../utils';
import './TestCalendar.css';

const TestCalendar = () => {
  // Calendar nav state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(CALENDAR_VIEWS.MONTH);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const {
    events, teams, environments, testTypes,
    loading, error,
    fetchEvents, createEvent, updateEvent, deleteEvent,
    changeStatus, extendEvent, closeEvent, isEventOwner,
  } = useCalendar();

  /**
   * Compute the date range to fetch based on view + currentDate
   */
  const getDateRange = useCallback(() => {
    let start, end;
    switch (view) {
      case 'week': {
        start = startOfWeek(currentDate, { weekStartsOn: 0 });
        end = endOfDay(addDays(start, 6));
        break;
      }
      case 'day': {
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
        break;
      }
      default: { // month
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
        break;
      }
    }
    return {
      start_date: formatForApi(start),
      end_date: formatForApi(end),
    };
  }, [currentDate, view]);

  /**
   * Load events when date/view/filters change
   */
  useEffect(() => {
    const range = getDateRange();
    const activeFilters = { ...range, page_size: '200' };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) activeFilters[k] = v;
    });
    fetchEvents(activeFilters);
  }, [currentDate, view, filters, fetchEvents, getDateRange]);

  // ---- Navigation ----
  const handleNavigate = (direction) => {
    setCurrentDate(navigateDate(currentDate, view, direction));
  };

  const handleToday = () => setCurrentDate(new Date());

  // ---- Filters ----
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ---- Day Click → switch to day view ----
  const handleDayClick = (day) => {
    setCurrentDate(day);
    setView(CALENDAR_VIEWS.DAY);
  };

  // ---- Event Click → detail dialog ----
  const handleEventClick = (evt) => {
    setSelectedEvent(evt);
    setDetailOpen(true);
  };

  // ---- Create / Edit ----
  const handleOpenCreate = () => {
    setEditEvent(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (evt) => {
    setEditEvent(evt);
    setDetailOpen(false);
    setFormOpen(true);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (editEvent?.id) {
        await updateEvent(editEvent.id, data);
        setSnackbar({ open: true, message: 'Event updated successfully', severity: 'success' });
      } else {
        await createEvent(data);
        setSnackbar({ open: true, message: 'Event created successfully', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Operation failed', severity: 'error' });
    }
  };

  // ---- Delete ----
  const handleDelete = async (eventId) => {
    try {
      await deleteEvent(eventId);
      setSnackbar({ open: true, message: 'Event deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Delete failed', severity: 'error' });
    }
  };

  // ---- Status / Extend / Close ----
  const handleChangeStatus = async (eventId, status) => {
    try {
      await changeStatus(eventId, status);
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Status change failed', severity: 'error' });
    }
  };

  const handleExtend = async (eventId, newEndDate, reason) => {
    try {
      await extendEvent(eventId, newEndDate, reason);
      setSnackbar({ open: true, message: 'Event extended', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Extend failed', severity: 'error' });
    }
  };

  const handleCloseEvent = async (eventId, status, reason) => {
    try {
      await closeEvent(eventId, status, reason);
      setSnackbar({ open: true, message: 'Event closed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Close failed', severity: 'error' });
    }
  };

  // ---- Refresh ----
  const handleRefresh = () => {
    const range = getDateRange();
    const activeFilters = { ...range, page_size: '200' };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) activeFilters[k] = v;
    });
    fetchEvents(activeFilters);
  };

  return (
    <Box className="test-calendar-page">
      {/* Page Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#01426A' }}>
          Test Calendar
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
          Plan, schedule, and track your testing and automation activities
        </Typography>
      </Box>

      {/* Error Banner */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <CalendarToolbar
        currentDate={currentDate}
        view={view}
        onNavigate={handleNavigate}
        onViewChange={setView}
        onTodayClick={handleToday}
        onCreateClick={handleOpenCreate}
        onRefresh={handleRefresh}
        loading={loading}
        filters={filters}
        onFilterChange={handleFilterChange}
        teams={teams}
        testTypes={testTypes}
        environments={environments}
      />

      {/* Calendar Grid */}
      <Box sx={{ mt: 2, position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, borderRadius: 3,
          }}>
            <Spinner size={40} color="#01426A" />
          </Box>
        )}
        <CalendarGrid
          currentDate={currentDate}
          view={view}
          events={events}
          onDayClick={handleDayClick}
          onEventClick={handleEventClick}
        />
      </Box>

      {/* Create / Edit Dialog */}
      <EventFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        event={editEvent}
        teams={teams}
        environments={environments}
        testTypes={testTypes}
      />

      {/* Detail Dialog */}
      <EventDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        event={selectedEvent}
        isOwner={selectedEvent ? isEventOwner(selectedEvent) : false}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        onChangeStatus={handleChangeStatus}
        onExtend={handleExtend}
        onCloseEvent={handleCloseEvent}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export { TestCalendar };
export default TestCalendar;
